/* eslint-disable max-lines */
import {
  BusinessHours,
  SOPWorkflow,
  SiteCategoryOption,
} from '@hakimo-ui/hakimo/types';
import {
  InputField,
  Textarea,
  Tooltip,
  SegmentedToggle,
  Label,
} from '@hakimo-ui/shared/ui-base';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { getInfoTextByFieldId } from '../utils';
import SOPCard from './SOPCard';
import {
  ZERO_TOLERANCE_OPTIONS,
  LONG_RANGE_ANALYSIS_OPTIONS,
} from '../constants';

interface Props {
  siteAddress: string;
  siteGoogleMapLocation: string;
  siteCategory: string;
  siteDescription: string;
  businessHours: BusinessHours;
  isZeroTolerance: boolean;
  isLongRangeAnalysisEnabled: boolean;
  siteCategoryOptions: SiteCategoryOption[];
  onChange: (data: Partial<SOPWorkflow>) => void;
  isEditing: boolean;
  timezone?: string;
}

export function SOPSiteInfoCard(props: Props) {
  const {
    siteAddress,
    siteGoogleMapLocation,
    siteCategory,
    siteDescription,
    businessHours,
    isZeroTolerance,
    isLongRangeAnalysisEnabled,
    siteCategoryOptions,
    onChange,
    isEditing,
    timezone,
  } = props;

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ siteAddress: e.target.value });
  };

  const handleMapLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ siteGoogleMapLocation: e.target.value });
  };

  const handleCategoryChange = (option: SiteCategoryOption) => {
    onChange({
      siteCategory: option.key,
      siteDescription: option.description,
    });
  };

  const handleDescriptionChange = (value: string) => {
    onChange({ siteDescription: value });
  };

  const handleBusinessHoursChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      businessHours: {
        hours: e.target.value,
        timezone: businessHours?.timezone || timezone || '',
      },
    });
  };

  const handleZeroToleranceChange = (value: string) => {
    onChange({ isZeroTolerance: value === 'enable' });
  };

  const handleLongRangeAnalysisChange = (value: string) => {
    onChange({ isLongRangeAnalysisEnabled: value === 'enable' });
  };

  const selectedCategory = siteCategoryOptions.find(
    (option) => option.key === siteCategory
  );

  return (
    <SOPCard title="Location Information">
      {isEditing ? (
        <div className="space-y-8">
          <div>
            <label className="mb-2 block">Address</label>
            <InputField
              value={siteAddress}
              type="text"
              onChange={handleAddressChange}
              placeholder="Enter site address..."
            />
          </div>
          <div>
            <label className="mb-2 block">Google Map Location</label>
            <InputField
              value={siteGoogleMapLocation}
              type="text"
              onChange={handleMapLocationChange}
              placeholder="Enter Google Map location URL..."
            />
          </div>
          <div>
            <label className="mb-2 block">Category</label>
            <SelectMenu<SiteCategoryOption>
              items={siteCategoryOptions}
              value={selectedCategory}
              onChange={handleCategoryChange}
              displayValue={(option) =>
                option?.key || 'Select site category...'
              }
              placeholder="Select site category..."
            />
          </div>
          <div>
            <label className="mb-2 block">Description</label>
            <Textarea
              value={siteDescription}
              onChange={handleDescriptionChange}
              placeholder="Enter site description..."
              rows={4}
            />
          </div>
          <div>
            <label className="mb-2 block">Business Hours</label>
            <InputField
              value={businessHours?.hours || ''}
              type="text"
              onChange={handleBusinessHoursChange}
              placeholder="Enter business hours..."
            />
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2">
              <label>Zero Tolerance</label>
              {getInfoTextByFieldId('isZeroTolerance') && (
                <Tooltip
                  text={getInfoTextByFieldId('isZeroTolerance') || ''}
                  size="large"
                  colorModifier="info"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </Tooltip>
              )}
            </div>
            <SegmentedToggle
              value={isZeroTolerance ? 'enable' : 'disable'}
              options={ZERO_TOLERANCE_OPTIONS}
              onChange={handleZeroToleranceChange}
              size="small"
            />
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2">
              <label>Long Range Analysis</label>
              {getInfoTextByFieldId('isLongRangeAnalysisEnabled') && (
                <Tooltip
                  text={
                    getInfoTextByFieldId('isLongRangeAnalysisEnabled') || ''
                  }
                  size="large"
                  colorModifier="info"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </Tooltip>
              )}
            </div>
            <SegmentedToggle
              value={isLongRangeAnalysisEnabled ? 'enable' : 'disable'}
              options={LONG_RANGE_ANALYSIS_OPTIONS}
              onChange={handleLongRangeAnalysisChange}
              size="small"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <div className="text-onlight-text-1 dark:text-ondark-text-1">
              Address
            </div>
            <div className="text-onlight-text-2 dark:text-ondark-text-2">
              {siteAddress || 'Not applicable'}
            </div>
          </div>
          <div>
            <div className="text-onlight-text-1 dark:text-ondark-text-1">
              Google Map Location
            </div>
            {siteGoogleMapLocation ? (
              <a
                href={siteGoogleMapLocation}
                target="_blank"
                rel="noreferrer"
                className="text-primary-500 break-words"
              >
                {siteGoogleMapLocation}
              </a>
            ) : (
              <div className="text-onlight-text-3 dark:text-ondark-text-3">
                Not applicable
              </div>
            )}
          </div>
          <div>
            <div className="text-onlight-text-1 dark:text-ondark-text-1">
              Category
            </div>
            <div className="text-onlight-text-2 dark:text-ondark-text-2">
              {siteCategory || 'Not applicable'}
            </div>
          </div>
          <div>
            <div className="text-onlight-text-1 dark:text-ondark-text-1">
              Description
            </div>
            <div className="text-onlight-text-2 dark:text-ondark-text-2 whitespace-pre-wrap">
              {siteDescription || 'Not applicable'}
            </div>
          </div>
          <div>
            <div className="text-onlight-text-1 dark:text-ondark-text-1">
              Business Hours
            </div>
            <div className="text-onlight-text-2 dark:text-ondark-text-2">
              {businessHours?.hours || 'Not applicable'}
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2">
              <label className="">Zero Tolerance</label>
              <Tooltip
                text={getInfoTextByFieldId('isZeroTolerance') || ''}
                size="large"
                colorModifier="info"
              >
                <InformationCircleIcon className="h-5 w-5" />
              </Tooltip>
            </div>
            {isZeroTolerance ? (
              <Label type="success" small>
                <div className="flex items-center gap-1">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>Enabled</span>
                </div>
              </Label>
            ) : (
              <Label type="default" small>
                <div className="flex items-center gap-1">
                  <XCircleIcon className="h-5 w-5" />
                  <span>Disabled</span>
                </div>
              </Label>
            )}
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2">
              <label className="">Long Range Analysis</label>
              <Tooltip
                text={getInfoTextByFieldId('isLongRangeAnalysisEnabled') || ''}
                size="large"
                colorModifier="info"
              >
                <InformationCircleIcon className="h-5 w-5" />
              </Tooltip>
            </div>
            {isLongRangeAnalysisEnabled ? (
              <Label type="success" small>
                <div className="flex items-center gap-1">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>Enabled</span>
                </div>
              </Label>
            ) : (
              <Label type="default" small>
                <div className="flex items-center gap-1">
                  <XCircleIcon className="h-5 w-5" />
                  <span>Disabled</span>
                </div>
              </Label>
            )}
          </div>
        </div>
      )}
    </SOPCard>
  );
}

export default SOPSiteInfoCard;
